import { useMemo, useState } from 'react';
import { postListFetcher } from '@/shared/api/list';
import { formatUnixDateTime } from '@/shared/lib/datetime';
import { GenericListPage } from '@/shared/ui/list/GenericListPage';
import type { ListPageConfig, ListRow } from '@/shared/ui/list/types';
import { PromoActionEditModal } from './PromoActionEditModal';

interface PartnerPromotion extends ListRow {
  id: number;
  service_id?: number;
  title?: string;
  description?: string;
  stop_message?: string | null;
  reject_message?: string | null;
  image_id?: number | string;
  city?: { id?: number; name?: string } | null;
  status_id?: number;
  tariff?: { id?: number; type?: string } | null;
  start_time?: number;
  end_time?: number;
}

const STATUS_NAMES: Record<number, string> = {
  0: 'Неактивная',
  1: 'Отправлена на модерацию',
  2: 'Модерация прошла успешно',
  3: 'Модерация отклонена',
  4: 'Акция остановлена',
  5: 'Акция запущена',
  6: 'Акция завершена',
  7: 'Акция отправлена в архив',
};

export const PromoActionsPage = () => {
  const [editing, setEditing] = useState<PartnerPromotion | null>(null);

  const config = useMemo<ListPageConfig<PartnerPromotion>>(
    () => ({
      queryKey: 'marketing-promo-actions',
      title: 'Акции партнеров',
      errorText: 'Ошибка загрузки акций партнеров',
      fetchList: postListFetcher('/service/promotion/list/'),
      rows: ['promotions'],
      /** As on test: no request until service_id or phone is set. */
      requireFilter: true,
      filters: [
        { name: 'service_id', label: 'ID сервиса', kind: 'text', placeholder: 'Поиск акции по ID СТО...' },
        { name: 'phone', label: 'Телефон сервиса', kind: 'text', placeholder: 'Поиск акции по телефону СТО...' },
      ],
      columns: [
        { header: 'ID акции', render: (promo) => promo.id },
        { header: 'ID сервиса', render: (promo) => promo.service_id },
        { header: 'Заголовок', render: (promo) => promo.title },
        { header: 'Город', render: (promo) => promo.city?.name },
        { header: 'Статус', render: (promo) => STATUS_NAMES[Number(promo.status_id)] },
        { header: 'ID тарифа', render: (promo) => `${promo.tariff?.id ?? ''} - дней` },
        { header: 'Тип тарифа', render: (promo) => promo.tariff?.type },
        { header: 'Время начала акции', render: (promo) => formatUnixDateTime(promo.start_time) },
        { header: 'Время конца акции', render: (promo) => formatUnixDateTime(promo.end_time) },
        {
          header: 'Edit',
          render: (promo) => (
            <button type="button" className="btn btn-success pull-right" onClick={() => setEditing(promo)}>
              Edit
            </button>
          ),
        },
      ],
    }),
    [],
  );

  return (
    <>
      <GenericListPage config={config} />
      {editing && <PromoActionEditModal promo={editing} onClose={() => setEditing(null)} />}
    </>
  );
};
